import * as THREE from "three";
import { cliffVisualOffset, smoothHeightsEdgeAware } from "@tribez/shared";
import { VERT_DIM, HEIGHT_VISUAL_SCALE } from "./constants";
import type { TeamSpawns } from "./types";
import { applyTerrainDetailShader } from "./terrain-shader";

// Biome palette — picked to fit the Viking flat-shaded look (`art-direction memory`).
// Haven heightmap spans -4.6 to 23.4m. Two playable elevations: center valley ~8m
// (main lane), upper plateaus ~18m (team spawns). True peaks above 19m are rare
// (~2% of cells). Saturations pushed past the original vibe so the world reads
// closer to League/Dota's hand-painted vibrance through the CSS canvas filter +
// vignette + ACES tone-mapping pipeline. Grass especially leans warm-yellow because
// flat olive reads as muddy-army at MOBA zoom.
// Primal re-grade (2026-06-24): the look is Far Cry Primal — LUSH GREEN grass
// under warm golden light, with earthy dirt PATHS + sun-baked CLIFFS giving the
// "primal" earth accents. Pass 1 over-earthed the grass to ochre (Aviv: "too far
// … more lush and green than that"); reverted to a lush, lightly sun-warmed green.
// The warmth + the red dawn/dusk drama live in the LIGHT (day-night-visual.ts),
// not in the grass colour.
const BIOME_GRASS = new THREE.Color(0x648f30);  // lush green, lightly sun-warmed
const BIOME_SAND  = new THREE.Color(0xddc070);  // +saturation
const BIOME_SNOW  = new THREE.Color(0xe8ecf2);
// Earthy dirt-brown for steep HILLSIDES. The cliff/hillside read now comes ENTIRELY
// from the terrain (no draped doodads): steep exaggerated elevation + this brown,
// feathered into the grass by slope so an impassable face reads as a continuous
// brown hillside that's part of the terrain — not a pasted-on wall (Aviv, 2026-06-30).
// A desaturated medium brown (vs the old orange 0xa0612f) so it reads as soil/rock,
// not rust, and sits in the same earthy family as the dirt PATH.
const BIOME_ROCK  = new THREE.Color(0x7a5a35);
// 1 - normal.y band: below = keep grass, above = full brown, smooth between. Walkable
// ramps top out ~45–50° (1-ny ≈ 0.29–0.36), so the band STARTS at 0.40 to keep every
// walkable slope green; it ramps to full brown by 0.72 (~73°). The wide window feathers
// the brown up the hillside instead of snapping a hard grass↔brown line.
const CLIFF_SLOPE_BAND = [0.40, 0.72];
// Trodden-path tone — warm tan stone that contrasts with cool grass for the
// readable color-rhythm League/Dota use to lay out lanes. Falls in the same
// hue family as cliff so the world doesn't feel palette-fragmented.
const BIOME_PATH  = new THREE.Color(0xbfa884);

// Path polyline goes team1.spawn → map center → team2.spawn. Distance from
// each vertex to the nearest segment drives a smoothstep-blended weight into
// the grass biome. PATH_INNER is the strong-color core; PATH_OUTER is where
// the path fades fully back to grass.
const PATH_INNER = 2.5;
const PATH_OUTER = 7.0;

// Soft biome boundaries: smoothstep windows blend neighbours instead of hard-snapping.
// `[low, high]` defines the transition band — fully one biome below `low`, fully the
// other above `high`. Numbers are tuned for Haven's elevation tiers.
const SAND_BAND  = [3.0, 5.5];   // sand → grass
const SNOW_BAND  = [17.5, 21.5]; // grass → snow

// Bilinearly-smoothed value noise sampled at a configurable cell size. fbm stacks 3
// octaves at 12m / 4m / 1.5m so the ground reads as multi-scale natural variation
// (large blobs of color, modulated by mid-scale patches, modulated by fine detail)
// rather than the uniform 4m grid we had before.
function valueHash(ix: number, iz: number): number {
  const v = Math.sin(ix * 12.9898 + iz * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
function smoothNoise(x: number, z: number, cell: number): number {
  const px = x / cell;
  const pz = z / cell;
  const ix = Math.floor(px);
  const iz = Math.floor(pz);
  const fx = px - ix;
  const fz = pz - iz;
  const a = valueHash(ix,     iz);
  const b = valueHash(ix + 1, iz);
  const c = valueHash(ix,     iz + 1);
  const d = valueHash(ix + 1, iz + 1);
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  return (a * (1 - sx) + b * sx) * (1 - sz) + (c * (1 - sx) + d * sx) * sz;
}
function fbm(x: number, z: number): number {
  return (
    smoothNoise(x, z, 12)  * 0.55 +
    smoothNoise(x, z, 4)   * 0.30 +
    smoothNoise(x, z, 1.5) * 0.15
  );
}
function smoothstep(low: number, high: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - low) / (high - low)));
  return t * t * (3 - 2 * t);
}

// Squared distance from point (px, pz) to segment (ax, az)→(bx, bz). Squared
// to skip a sqrt per-vertex; caller takes one sqrt at the very end.
function distSqToSegment(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) {
    const ex = px - ax;
    const ez = pz - az;
    return ex * ex + ez * ez;
  }
  let t = ((px - ax) * dx + (pz - az) * dz) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cz = az + t * dz;
  const ex = px - cx;
  const ez = pz - cz;
  return ex * ex + ez * ez;
}

const CLIFF_CELL_DIM = 256;  // cliffs.bin grid (VERT_DIM - 1)

// Cliff level of the cell a vertex belongs to (clamp the +1 edge row/col into range).
function levelAtVertex(cliffLevels: Uint16Array, row: number, col: number): number {
  const r = row >= CLIFF_CELL_DIM ? CLIFF_CELL_DIM - 1 : row;
  const c = col >= CLIFF_CELL_DIM ? CLIFF_CELL_DIM - 1 : col;
  return cliffLevels[r * CLIFF_CELL_DIM + c]!;
}

// VISUAL terrain smoothing. Delegates to the SHARED edge-aware blur (smoothHeightsEdgeAware
// in @tribez/shared) so the rendered mesh and the navmesh bake derive from the SAME ramp-
// aware surface — they can't drift. Used only for the visual mesh; gameplay sampling keeps
// raw heights. HARD plateau↔plateau lips stay crisp (excluded every pass) so cliffs read as
// walls; ramps/slopes blend so an authored ramp reads as a gentle WALKABLE slope (which the
// exaggeration would otherwise steepen toward a cliff). The navmesh runs MORE passes than
// this (its ramp steps must drop below recast's walkableClimb); the client needs only a few.
const SMOOTH_PASSES = 3;

export function smoothHeightsForVisuals(
  src: Float32Array,
  cliffLevels: Uint16Array,
  passes: number = SMOOTH_PASSES,
): Float32Array {
  return smoothHeightsEdgeAware(src, cliffLevels, passes);
}

// Cliff-step exaggeration (CLIENT-ONLY visual height boost). SC2's synced heightmap is
// a smoothed pathing echo, so real cliff drops collapsed to ~1m and read as gentle
// slopes, not walls. We lift each higher cliff LEVEL in the visual height field so the
// plateaus separate vertically into KTS-style cliffs. Level 64 (the main lane, 59% of
// the map) is the base and stays put; 128/192 rise by 1×/2× CLIFF_EXTRA_PER_LEVEL; ramp
// cells carry intermediate cliff values so their offset interpolates → they stay
// walkable slopes onto the raised plateaus. The server's raw heights + navmesh are
// untouched (this is the visual layer only); the cliff faces derive from this same
// field so they grow tall in lockstep with the terrain (no floating rim). The per-
// level boost lives in @tribez/shared (cliffVisualOffset) so the doodad flat-ground
// filter measures float against this exact surface.
export function exaggerateCliffHeights(src: Float32Array, cliffLevels: Uint16Array): Float32Array {
  const out = new Float32Array(src.length);
  for (let row = 0; row < VERT_DIM; row++) {
    for (let col = 0; col < VERT_DIM; col++) {
      const lvl = levelAtVertex(cliffLevels, row, col);
      // Off-map (0) and the base level add 0 (no sink); higher levels lift.
      const i = row * VERT_DIM + col;
      out[i] = src[i]! + cliffVisualOffset(lvl);
    }
  }
  return out;
}

export function buildTerrain(visualHeights: Float32Array, spawns: TeamSpawns): THREE.Mesh {
  const verts = new Float32Array(VERT_DIM * VERT_DIM * 3);
  for (let row = 0; row < VERT_DIM; row++) {
    for (let col = 0; col < VERT_DIM; col++) {
      const i = row * VERT_DIM + col;
      verts[i * 3 + 0] = col;
      verts[i * 3 + 1] = visualHeights[i]! * HEIGHT_VISUAL_SCALE;
      verts[i * 3 + 2] = row;
    }
  }
  const indices = new Uint32Array((VERT_DIM - 1) * (VERT_DIM - 1) * 6);
  let k = 0;
  for (let row = 0; row < VERT_DIM - 1; row++) {
    for (let col = 0; col < VERT_DIM - 1; col++) {
      const tl = row * VERT_DIM + col;
      const tr = tl + 1;
      const bl = tl + VERT_DIM;
      const br = bl + 1;
      indices[k++] = tl; indices[k++] = bl; indices[k++] = tr;
      indices[k++] = tr; indices[k++] = bl; indices[k++] = br;
    }
  }
  const geom = new THREE.BufferGeometry();
  geom.setAttribute("position", new THREE.BufferAttribute(verts, 3));
  geom.setIndex(new THREE.BufferAttribute(indices, 1));
  geom.computeVertexNormals();
  const normAttr = geom.attributes.normal as THREE.BufferAttribute;

  // Per-vertex biome colors with soft boundaries. Each vertex computes 3 weights
  // (snow, sand, grass) from smoothstepped height and blends the biome base colors.
  // Cliff-brown recolor removed — steep terrain keeps its biome color; rock reads
  // come from the real cliff-face geometry (cliff-faces.ts). Lightness jitter from
  // fbm noise is scaled by the grass+sand weight: only those biomes vary, keeping
  // snow from getting splotchy.
  //
  // Trodden path: KTS itself shipped no hand-placed path doodads / regions /
  // texture splats (verified by subagent search of `reference/extracted/`).
  // Best gameplay-significant proxy is the team-axis through the two Rezz
  // Shrines — those are the natural midpoints between team spawns and the
  // KTS designer placed them on the same diagonal as team-spawn flow.
  // Coords pulled from `regions.json`: SouthRezzShrine (71.5, 67.5),
  // NorthRezzShrine (193.5, 195.0).
  const t1 = spawns.twoTeam.team1;
  const t2 = spawns.twoTeam.team2;
  const SOUTH_REZZ = { x: 71.5, y: 67.5 };
  const NORTH_REZZ = { x: 193.5, y: 195.0 };
  const pathSegments: Array<[number, number, number, number]> = [
    [t1.x, t1.y, SOUTH_REZZ.x, SOUTH_REZZ.y],
    [SOUTH_REZZ.x, SOUTH_REZZ.y, NORTH_REZZ.x, NORTH_REZZ.y],
    [NORTH_REZZ.x, NORTH_REZZ.y, t2.x, t2.y],
  ];
  const pathOuterSq = PATH_OUTER * PATH_OUTER;

  const colors = new Float32Array(VERT_DIM * VERT_DIM * 3);
  const hsl = { h: 0, s: 0, l: 0 };
  const tmp = new THREE.Color();
  for (let row = 0; row < VERT_DIM; row++) {
    for (let col = 0; col < VERT_DIM; col++) {
      const i = row * VERT_DIM + col;
      const h = verts[i * 3 + 1]!;

      const wSnow  = smoothstep(SNOW_BAND[0]!,  SNOW_BAND[1]!,  h);
      const wSand  = 1 - smoothstep(SAND_BAND[0]!, SAND_BAND[1]!, h);
      const wGrass = Math.max(0, 1 - wSnow - wSand);

      // Path weight: 1 inside PATH_INNER, 0 outside PATH_OUTER, smooth between.
      // Gated by grass × (1 - snow) so it only shows on grassy terrain.
      let pathDistSq = pathOuterSq;
      for (const [ax, az, bx, bz] of pathSegments) {
        const d2 = distSqToSegment(col, row, ax, az, bx, bz);
        if (d2 < pathDistSq) pathDistSq = d2;
      }
      const pathDist = Math.sqrt(pathDistSq);
      const pathBase = 1 - smoothstep(PATH_INNER, PATH_OUTER, pathDist);
      const pathWeight = pathBase * wGrass * (1 - wSnow);

      // Blend by height-driven weights. Result is in linear-ish RGB; jitter applies
      // via HSL after. Rock color now comes from real cliff-face geometry only.
      const r = (BIOME_SAND.r * wSand + BIOME_GRASS.r * wGrass + BIOME_SNOW.r * wSnow);
      const g = (BIOME_SAND.g * wSand + BIOME_GRASS.g * wGrass + BIOME_SNOW.g * wSnow);
      const b = (BIOME_SAND.b * wSand + BIOME_GRASS.b * wGrass + BIOME_SNOW.b * wSnow);
      tmp.setRGB(r, g, b);

      // Apply path tone — lerp toward BIOME_PATH by pathWeight. Done before the
      // fbm jitter so the path inherits per-vertex lightness variation and reads
      // as worn stone rather than dead-flat color.
      if (pathWeight > 0.001) {
        tmp.lerp(BIOME_PATH, pathWeight);
      }

      // Steep cliff faces → warm rock, by slope. This is what stops an exposed cliff
      // face (or the ground under a sparse rock-mesh drape) from rendering near-black.
      const ny = normAttr.getY(i);
      const wRock = smoothstep(CLIFF_SLOPE_BAND[0]!, CLIFF_SLOPE_BAND[1]!, 1 - ny);
      if (wRock > 0.001) {
        tmp.lerp(BIOME_ROCK, wRock);
      }

      // Jitter: ±9% lightness from fbm, gated to the grass+sand+path portion of the
      // blend and faded out on rock (rock shouldn't get the grassy splotch jitter).
      const grassSandWeight = (wSand + wGrass + pathWeight * 0.6) * (1 - wRock);
      if (grassSandWeight > 0.05) {
        const n = fbm(col, row);  // 0..1
        const dl = (n - 0.5) * 2 * 0.09 * grassSandWeight;
        tmp.getHSL(hsl);
        hsl.l = Math.min(1, Math.max(0, hsl.l + dl));
        tmp.setHSL(hsl.h, hsl.s, hsl.l);
      }
      colors[i * 3 + 0] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }
  }
  geom.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  // Smooth-shaded Lambert: `flatShading: false` lets the averaged vertex normals
  // give a continuous lighting gradient across the heightmap, which combined with
  // the visual-only gaussian smooth above makes elevation changes read as
  // gradations instead of flat triangle facets. Doodads keep flatShading for the
  // chunky Quaternius look — terrain is the only mesh that benefits from smooth.
  const mat = new THREE.MeshLambertMaterial({
    vertexColors: true,
  });
  // Sub-meter ground detail + grass mottling — injected via onBeforeCompile.
  // See world/terrain-shader.ts for the full GLSL + rationale.
  applyTerrainDetailShader(mat);
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = "terrain";
  mesh.receiveShadow = true;

  // Edge extension (issue #4): the playable mesh hard-cuts at the 0..256 boundary
  // (a visible edge line). Continue the ground OUTWARD at the same edge height
  // with the same material, so the surface flows off the boundary seamlessly and
  // the player can't see where it ends (fog hides it first). Added as a CHILD so
  // it travels with the terrain but stays out of terrain raycasts (intersectObject
  // is non-recursive) and the FoW shroud (which clones the parent geometry only).
  mesh.add(buildTerrainSkirt(verts, colors));
  return mesh;
}

// Radial extrusion of the terrain perimeter, continuing the playable ground
// OUTWARD into the fog so the player can't see where the world ends. Ring 0 sits
// exactly on the boundary vertices (positions + colours), and the near/mid rings
// stay at the SAME edge HEIGHT (zero drop) so the terrain's top surface flows
// straight off the boundary with no silhouette step — the height step was what
// read as a "hard edge". Only the far ring tapers down, and it sits past fog's
// ~270u near-total cutoff, so the taper is never seen. ~210u of level ground
// before then means fog hides the end before geometry does. The SAME detail
// shader (world-XZ based → continuous across the seam) + matching MeshLambert
// vertexColors make the surface seamless with the playable terrain.
// A finer ring near the edge (~22u, the day sight radius) so the skirt's
// distance-based fog falloff (fog.ts) is sampled finely where vision actually
// fades, not interpolated coarsely across one big gap.
const SKIRT_RING_OUT = [0, 22, 55, 110, 210, 340];   // radial offset from the edge
const SKIRT_RING_DROP = [0, 0, 0, 0, 12, 60];        // height drop per ring (level until far)

// Height the skirt has dropped at `distFromEdge` units outward (piecewise-linear
// over the ring table above). Exported so border decor (world/doodads.ts) can
// seat trees ON the collar instead of floating at flat edge height once the skirt
// begins to taper down past ~110u.
export function skirtDrop(distFromEdge: number): number {
  if (distFromEdge <= 0) return 0;
  for (let k = 1; k < SKIRT_RING_OUT.length; k++) {
    if (distFromEdge <= SKIRT_RING_OUT[k]!) {
      const t = (distFromEdge - SKIRT_RING_OUT[k - 1]!) / (SKIRT_RING_OUT[k]! - SKIRT_RING_OUT[k - 1]!);
      return SKIRT_RING_DROP[k - 1]! + t * (SKIRT_RING_DROP[k]! - SKIRT_RING_DROP[k - 1]!);
    }
  }
  return SKIRT_RING_DROP[SKIRT_RING_DROP.length - 1]!;
}

function buildTerrainSkirt(verts: Float32Array, colors: Float32Array): THREE.Mesh {
  const N = VERT_DIM;
  const cx = (N - 1) / 2;
  const cz = (N - 1) / 2;

  // Ordered perimeter loop of boundary vertices (top → right → bottom → left).
  const loop: number[] = [];
  const push = (row: number, col: number) => loop.push(row * N + col);
  for (let col = 0; col < N; col++) push(0, col);
  for (let row = 1; row < N; row++) push(row, N - 1);
  for (let col = N - 2; col >= 0; col--) push(N - 1, col);
  for (let row = N - 2; row >= 1; row--) push(row, 0);

  const rings = SKIRT_RING_OUT.length;
  const count = loop.length;
  const pos = new Float32Array(count * rings * 3);
  const col = new Float32Array(count * rings * 3);

  for (let i = 0; i < count; i++) {
    const vi = loop[i]!;
    const px = verts[vi * 3 + 0]!;
    const ph = verts[vi * 3 + 1]!;
    const pz = verts[vi * 3 + 2]!;
    // Outward radial direction from the map centre (perpendicular-ish at edges,
    // diagonal at corners) — naturally fans the collar without corner seams.
    let dx = px - cx, dz = pz - cz;
    const dl = Math.hypot(dx, dz) || 1;
    dx /= dl; dz /= dl;
    const r = colors[vi * 3 + 0]!, g = colors[vi * 3 + 1]!, b = colors[vi * 3 + 2]!;
    for (let k = 0; k < rings; k++) {
      const o = (k * count + i) * 3;
      pos[o + 0] = px + dx * SKIRT_RING_OUT[k]!;
      pos[o + 1] = ph - SKIRT_RING_DROP[k]!;
      pos[o + 2] = pz + dz * SKIRT_RING_OUT[k]!;
      col[o + 0] = r; col[o + 1] = g; col[o + 2] = b;
    }
  }

  const idx: number[] = [];
  for (let k = 0; k < rings - 1; k++) {
    for (let i = 0; i < count; i++) {
      const i2 = (i + 1) % count;
      const a = k * count + i;
      const bb = k * count + i2;
      const c = (k + 1) * count + i;
      const d = (k + 1) * count + i2;
      idx.push(a, c, bb, bb, c, d);
    }
  }

  const sgeom = new THREE.BufferGeometry();
  sgeom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  sgeom.setAttribute("color", new THREE.BufferAttribute(col, 3));
  sgeom.setIndex(idx);
  sgeom.computeVertexNormals();

  // SAME material recipe as the playable terrain — Lambert + vertexColors + the
  // world-XZ detail shader — so the ground texture is continuous across the seam
  // (no brightness/texture line). DoubleSide so winding never culls the collar.
  const smat = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
  applyTerrainDetailShader(smat);
  const skirt = new THREE.Mesh(sgeom, smat);
  skirt.name = "terrain-skirt";
  skirt.receiveShadow = true;
  return skirt;
}
